import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Camera } from '../../../core/domain/camera';
import type { CameraStreamManager } from './camera-stream-manager';
import { overviewCard, statusBadge } from '@vitesse/base-ui/components/templates';
import { cameraStatusBadges, type CameraFeedStats } from './camera-badges';
import './camera-feed';

/*
 * <vitesse-camera-card> — the ONE camera card, rendered identically wherever a
 * camera appears (gateway detail, home, a future dashboard). Compact by design:
 * title + base status badges (Connected · kbps · latency) + the live feed
 * thumbnail, so it matches the height of every other overview card. The live
 * media metadata is REAL base badges in the card chrome — never overlaid on the
 * video — fed by the feed's `feedstats` event (bubbles up to this host).
 *
 * The preview IS the live feed (<vitesse-camera-feed variant="preview">): it
 * goes live while the card is on-screen and pauses when scrolled away or the tab
 * hides (the look-away lifecycle), so an off-screen grid costs nothing.
 */
@customElement('vitesse-camera-card')
export class CameraCard extends LitElement {
  @property({ attribute: false }) camera?: Camera;
  @property() gatewayId = '';
  /** Friendly gateway label — shown as a leading badge on the flat home views so
   *  same-named cameras across gateways stay distinguishable; omitted on the
   *  gateway detail page (already scoped to one gateway). */
  @property() gatewayName = '';
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  /** Cloud (relayed) grids set this → the preview is a refreshed snapshot poster
   *  that goes live on hover, instead of a live feed (saves Hetzner egress). LAN
   *  leaves it false → always-live preview. */
  @property({ type: Boolean }) poster = false;

  @state() private stats?: CameraFeedStats;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('feedstats', this.onFeedStats as EventListener);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('feedstats', this.onFeedStats as EventListener);
  }
  private readonly onFeedStats = (e: CustomEvent<CameraFeedStats>): void => {
    this.stats = e.detail;
  };

  private nav(): void {
    if (!this.camera) return;
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'camera', gatewayId: this.gatewayId, cameraId: this.camera.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    const c = this.camera;
    if (!c) return nothing;
    return overviewCard({
      title: c.name,
      entityType: 'camera',
      clickable: true,
      onClick: () => this.nav(),
      // Gateway badge FIRST (the cross-gateway distinguisher, like every other
      // flat-home card), then live connection/bitrate/latency. All in the card
      // chrome, none on the video.
      badges: html`${this.gatewayName ? statusBadge('gateway', this.gatewayName, 'hard-drive') : nothing}${cameraStatusBadges(
        c,
        this.stats,
      )}`,
      preview: html`<vitesse-camera-feed
        variant="preview"
        .gatewayId=${this.gatewayId}
        .cameraId=${c.id}
        .cameraStreams=${this.cameraStreams}
        ?poster=${this.poster}
      ></vitesse-camera-feed>`,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-camera-card': CameraCard;
  }
}
